import { NavLink } from "react-router-dom";
import {
  Box,
  StackLayout,
  focusVisibleOverrideOuter,
  styled,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { useJourneyNavEnabled } from "hooks/use-journey-nav-enabled/use-journey-nav-enabled.hook";

// Text-only nav link, bold, $buttonTwo typography, with an underline
// indicator under the active page. No icons.
const StyledTabLink = styled(NavLink, {
  display: "flex",
  position: "relative",
  alignItems: "center",
  color: "$onSurfaceTextSubdued",
  typography: "$buttonTwo",
  textDecoration: "none !important",
  cursor: "pointer",
  justifyContent: "center",
  padding: "$none $half",
  height: "100%",
  ...focusVisibleOverrideOuter,
  "&:hover": {
    color: "$onSurfaceTextPrimary",
  },
  '&[aria-current="page"]': {
    color: "$onSurfaceTextPrimary",
  },
  '&[aria-current="page"]:after': {
    content: '""',
    display: "block",
    position: "absolute",
    bottom: -1,
    insetInlineStart: 0,
    backgroundColor: "$interactiveActionPrimary",
    height: "$quarter",
    borderRadius: "$large",
    width: "100%",
  },
});

const TabItem = styled("li", {
  display: "flex",
  alignSelf: "stretch",
  margin: "$none $one",
  "@tablet": {
    margin: "$none $half",
  },
});

type TabDef = {
  to: string;
  labelKey: `STR_${string}`;
};

const TABS: ReadonlyArray<TabDef> = [
  { to: APP_MANAGED_ROUTES.HOME, labelKey: "STR_TAB_HOME" },
  { to: APP_MANAGED_ROUTES.BENEFITS, labelKey: "STR_TAB_BENEFITS" },
  { to: APP_MANAGED_ROUTES.CARE, labelKey: "STR_TAB_CARE" },
  { to: APP_MANAGED_ROUTES.CLAIMS, labelKey: "STR_TAB_CLAIMS" },
  { to: APP_MANAGED_ROUTES.JOURNEY, labelKey: "STR_TAB_JOURNEY" },
];

export const AppTabs = () => {
  const { formatMessage } = useIntl();
  const { enabled: journeyNavEnabled } = useJourneyNavEnabled();
  const tabs = journeyNavEnabled
    ? TABS
    : TABS.filter((t) => t.to !== APP_MANAGED_ROUTES.JOURNEY);

  return (
    <Box as="nav" aria-label="Primary navigation" css={{ height: "100%" }}>
      <StackLayout
        as="ul"
        orientation="horizontal"
        verticalAlignment="center"
        css={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          height: "100%",
          gap: 0,
        }}
      >
        {tabs.map((tab) => (
          <TabItem key={tab.to}>
            <StyledTabLink to={tab.to} end={tab.to === APP_MANAGED_ROUTES.HOME}>
              <Box as="span">{formatMessage({ id: tab.labelKey })}</Box>
            </StyledTabLink>
          </TabItem>
        ))}
      </StackLayout>
    </Box>
  );
};
